import { IkeEnvironment, type IkeSystem } from '@ikegps/core-api';
import { IkeNumber } from '../util';
import type { IkeConfigKey } from './store.keys';

export interface IkeConfigJSON {
	env: IkeEnvironment;
	system: IkeSystem;
	keys: any;
}

/**
 * A service which utilizes IkeConfig and binds to life cycle events.
 */
export interface IkeConfigService {
	close: () => Promise<void>;
}

/**
 *
 */
export class IkeConfig {
	public system: IkeSystem;
	public env: IkeEnvironment;

	public config;

	private services: IkeConfigService[] = [];

	constructor(
		system: IkeSystem,
		env: IkeEnvironment,
		defaults?: Partial<{
			[key in IkeConfigKey]: any;
		}>
	) {
		this.system = system;
		this.env = env;
		// This allows a config to be created with defaults. Useful
		// when passing variables into the client-side app from the build
		// pipeline or web server
		this.config = { ...defaults };
	}

	/**
	 * Is this config for a production like environment
	 *
	 * eg Production or Preview
	 */
	get isProduction(): boolean {
		return this.env === IkeEnvironment.PRODUCTION;
	}

	set(key: IkeConfigKey, value: unknown): void {
		this.config[key] = value;
	}

	/**
	 * Determine if the key has a value
	 *
	 * not null or not empty string
	 *
	 * @param key key to check
	 */
	has(key: IkeConfigKey): boolean {
		const val = this.config[key];
		return val != null && val !== '';
	}

	/** Unset a key */
	delete(key: IkeConfigKey): void {
		delete this.config[key];
	}

	/**
	 *
	 * @param key Config key to fetch
	 * @deprecated Use specific typed getters instead
	 */
	get(key: IkeConfigKey) {
		return this.config[key];
	}

	/**
	 * Converts a config value to a number if it is a number
	 * @returns value as a number (float) null if the value could not be parsed or undefined if the key is not set
	 */
	getNumber(key: IkeConfigKey) {
		const val = this.config[key];
		if (val === undefined) {
			return undefined;
		}
		return IkeNumber.toNumber(this.config[key]);
	}

	/**
	 * attempts to convert a config value to a boolean
	 * boolean true and string 'true' return true
	 * all other values return false
	 * @param key Config key to fetch
	 * @returns value as a boolean (true/false)
	 */
	getBoolean(key: IkeConfigKey): boolean {
		const val = this.config[key];
		if (val == null) {
			return false;
		}
		if (typeof val === 'boolean') {
			return val;
		}
		if (typeof val === 'string') {
			return val.toLowerCase() === 'true';
		}
		return false;
	}

	getString(key: IkeConfigKey): string | undefined {
		const val = this.config[key];
		if (val == null) {
			return undefined;
		}
		if (typeof val === 'string') {
			return val;
		}
		if (typeof val === 'number') {
			return val.toString();
		}
		if (typeof val === 'boolean') {
			return val ? 'true' : 'false';
		}
		if (Array.isArray(val)) {
			return val.join(',');
		}
		if (typeof val === 'object') {
			return JSON.stringify(val);
		}
		return undefined;
	}

	mustGetString(key: IkeConfigKey): string {
		const value = this.getString(key);

		if (value === undefined) {
			throw new Error(`IkeConfig.mustGetString: required key "${key}" was not found in the config`);
		}

		return value;
	}

	/**
	 * Get a config key if it is null return the default value
	 *
	 * @param key Config key to fetch
	 * @param defaultValue Default value to return if key is not found
	 */
	getDefaultNumber(key: IkeConfigKey, defaultValue: number): number {
		const num = this.getNumber(key);
		if (num == null) {
			return defaultValue;
		}
		return num;
	}

	/**
	 * Check whether all the keys are present in the config.
	 *
	 * @param keys List of keys that are required .
	 * @returns True if a key is missing, false otherwise.
	 */
	requires(keys: IkeConfigKey[]): boolean {
		return keys.find((k) => this.config[k] == null) != null;
	}

	missing(keys: IkeConfigKey[]): string[] {
		return keys.filter((k) => this.config[k] == null);
	}

	/**
	 * Register that a service was created with this config,
	 * This is used to close the service we the config is destroyed
	 * @param service
	 */
	register(service: IkeConfigService) {
		this.services.push(service);
	}

	/** Wait for all services that have been bound to this account to close before closing the config */
	async close() {
		await Promise.all(this.services.map((s) => s.close()));
		this.services = [];
	}

	/**
	 * Copy all config variables from another IkeConfig
	 * @param other
	 */
	extend(other: IkeConfig) {
		Object.keys(other.toJSON().keys).forEach((k) => this.set(k as IkeConfigKey, other.get(k as IkeConfigKey)));
	}

	toJSON(): IkeConfigJSON {
		return {
			env: this.env,
			system: this.system,
			keys: this.config,
		};
	}

	toString() {
		return `Config: ${this.system}:${this.env}`;
	}
}

export class IkeConfigStore {
	static stores: { [key: string]: IkeConfig } = {};

	/** Fetch API implementation — must be set explicitly by consumers before use. */
	static fetch: typeof globalThis.fetch;

	static readonly MAX_SEARCH_LENGTH = 512;

	static get(system?: IkeSystem, env?: IkeEnvironment): IkeConfig {
		var key = IkeConfigStore.makeKey(system, env);
		var store = IkeConfigStore.stores[key];
		if (store == null) {
			throw new Error('Invalid system/environment combination: ' + key);
		}
		return store;
	}

	static register(cfg: IkeConfig) {
		var key = IkeConfigStore.makeKey(cfg.system, cfg.env);
		var store = IkeConfigStore.stores[key];
		if (store != null) {
			throw new Error('Store has already been registered: ' + cfg.toString());
		}
		IkeConfigStore.stores[key] = cfg;
	}

	private static makeKey(system: IkeSystem, env: IkeEnvironment): string {
		return `${system}:${env}`.toLowerCase();
	}
}
